import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { User, Disc3, Search, Loader2, Download, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Sparkles, Play, Shuffle, ListMusic } from 'lucide-react'
import { artists, importer } from '../api/client'
import { usePlayerStore } from '../stores/player'
import type { TrackWithDetails } from '../types'
import type { DabSearchResult } from '../api/client'

export default function Artist() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const playQueue = usePlayerStore((state) => state.playQueue)

  // State for missing albums section
  const [showMissingAlbums, setShowMissingAlbums] = useState(false)
  const [dabDownloadingIds, setDabDownloadingIds] = useState<Set<string>>(new Set())
  const [dabDownloadedIds, setDabDownloadedIds] = useState<Set<string>>(new Set())
  const [dabDownloadErrors, setDabDownloadErrors] = useState<Record<string, string>>({})

  const { data: artist, isLoading } = useQuery({
    queryKey: ['artists', id],
    queryFn: () => artists.get(id!),
    enabled: !!id,
  })

  const { data: artistTracks = [], isLoading: tracksLoading } = useQuery({
    queryKey: ['artists', id, 'tracks'],
    queryFn: () => artists.tracks(id!),
    enabled: !!id,
  })

  // Search DAB for this artist when section is expanded
  const { data: dabResults, isLoading: dabSearching } = useQuery({
    queryKey: ['dab-search-artist', artist?.name],
    queryFn: () => importer.searchDab(artist!.name),
    enabled: showMissingAlbums && !!artist?.name,
  })

  // Helper to normalize strings for comparison
  const normalizeForComparison = (s: string): string => {
    return s
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const sortedArtistTracks = useMemo(() => {
    return [...artistTracks].sort((a, b) => {
      const albumA = artist?.albums.find(album => album.id === a.album_id)
      const albumB = artist?.albums.find(album => album.id === b.album_id)
      const yearA = albumA?.year ?? 9999
      const yearB = albumB?.year ?? 9999
      return yearB - yearA
        || (albumA?.title || a.album_title || '').localeCompare(albumB?.title || b.album_title || '')
        || (a.disc_number || 0) - (b.disc_number || 0)
        || (a.track_number || 9999) - (b.track_number || 9999)
        || a.title.localeCompare(b.title)
    })
  }, [artist?.albums, artistTracks])

  const tracksByAlbum = useMemo(() => {
    const groups: Array<{ albumId: string; title: string; year: number | null; coverPath: string | null; tracks: TrackWithDetails[] }> = []
    const groupMap = new Map<string, typeof groups[number]>()

    sortedArtistTracks.forEach((track) => {
      const album = artist?.albums.find(a => a.id === track.album_id)
      const albumId = track.album_id || 'unknown'
      let group = groupMap.get(albumId)
      if (!group) {
        group = {
          albumId,
          title: album?.title || track.album_title || 'Unknown Album',
          year: album?.year ?? null,
          coverPath: album?.cover_path || track.cover_path || null,
          tracks: [],
        }
        groupMap.set(albumId, group)
        groups.push(group)
      }
      group.tracks.push(track)
    })

    return groups
  }, [artist?.albums, sortedArtistTracks])

  const handlePlayAll = () => {
    if (sortedArtistTracks.length > 0) {
      playQueue(sortedArtistTracks, 0)
    }
  }

  const handleShuffleAll = () => {
    if (sortedArtistTracks.length === 0) return
    const shuffled = [...sortedArtistTracks]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    playQueue(shuffled, 0)
  }

  const formatDuration = (ms: number | null) => {
    if (!ms) return '--:--'
    const seconds = Math.floor(ms / 1000)
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Filter DAB results to only show albums not in library
  const missingAlbums = useMemo(() => {
    if (!dabResults || !artist) return []

    const existingTitles = new Set(
      artist.albums.map(album => normalizeForComparison(album.title))
    )

    return dabResults.filter(result => {
      // Check if this artist matches (DAB might return albums by similar artists)
      const dabArtist = normalizeForComparison(result.artist)
      const ourArtist = normalizeForComparison(artist.name)
      if (!dabArtist.includes(ourArtist) && !ourArtist.includes(dabArtist)) {
        return false
      }
      // Check if we already have this album
      const dabTitle = normalizeForComparison(result.title)
      return !existingTitles.has(dabTitle)
    })
  }, [dabResults, artist])

  // DAB download mutation
  const downloadDabMutation = useMutation({
    mutationFn: async (result: DabSearchResult) => {
      return importer.downloadDabStandalone(result.album_id, result.artist, result.title)
    },
    onMutate: (result) => {
      setDabDownloadingIds(prev => new Set(prev).add(result.album_id))
      setDabDownloadErrors(prev => {
        const next = { ...prev }
        delete next[result.album_id]
        return next
      })
    },
    onSuccess: (data, result) => {
      setDabDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.album_id)
        return next
      })
      if (data.success) {
        setDabDownloadedIds(prev => new Set(prev).add(result.album_id))
        // Invalidate queries to refresh the artist page
        queryClient.invalidateQueries({ queryKey: ['artists', id] })
        queryClient.invalidateQueries({ queryKey: ['tracks'] })
        queryClient.invalidateQueries({ queryKey: ['albums'] })
      } else {
        setDabDownloadErrors(prev => ({
          ...prev,
          [result.album_id]: data.message || 'Download failed'
        }))
      }
    },
    onError: (error: unknown, result) => {
      setDabDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.album_id)
        return next
      })
      let errorMsg = 'Download failed'
      if (error && typeof error === 'object') {
        const axiosError = error as { response?: { data?: string }; message?: string }
        errorMsg = axiosError.response?.data || axiosError.message || 'Download failed'
      }
      setDabDownloadErrors(prev => ({
        ...prev,
        [result.album_id]: errorMsg
      }))
    },
  })

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Loading...</div>
      </div>
    )
  }

  if (!artist) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Artist not found</div>
      </div>
    )
  }

  // Use first album cover as fallback for artist image
  const artistImage = artist.image_path || artist.albums.find(a => a.cover_path)?.cover_path

  return (
    <div className="p-4 sm:p-6">
      {/* Artist header - stacks on mobile */}
      <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="w-32 h-32 sm:w-48 sm:h-48 bg-dark-700 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
          {artistImage ? (
            <img
              src={`/api/image/${encodeURIComponent(artistImage)}`}
              alt={artist.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <User size={48} className="sm:w-16 sm:h-16 text-dark-500" />
          )}
        </div>
        <div className="text-center sm:text-left">
          <p className="text-xs sm:text-sm text-dark-400 uppercase tracking-wider mb-1 sm:mb-2">Artist</p>
          <h1 className="text-2xl sm:text-4xl font-bold mb-2">{artist.name}</h1>
          <p className="text-dark-400">
            {artist.albums.length} album{artist.albums.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Biography */}
      {artist.biography && (
        <div className="mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">About</h2>
          <p className="text-sm sm:text-base text-dark-300 leading-relaxed">{artist.biography}</p>
        </div>
      )}

      {/* Albums */}
      <div className="mb-8">
        <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Albums</h2>
        {artist.albums.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
            {artist.albums.map((album) => (
              <Link
                key={album.id}
                to={`/albums/${album.id}`}
                className="card group hover:bg-dark-700 transition-colors"
              >
                <div className="aspect-square mb-3 sm:mb-4 bg-dark-700 rounded-lg flex items-center justify-center overflow-hidden">
                  {album.cover_path ? (
                    <img
                      src={`/api/image/${encodeURIComponent(album.cover_path)}`}
                      alt={album.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Disc3 size={32} className="sm:w-12 sm:h-12 text-dark-500" />
                  )}
                </div>
                <h3 className="text-sm sm:text-base font-medium truncate group-hover:text-primary-400">
                  {album.title}
                </h3>
                {album.year && (
                  <p className="text-xs sm:text-sm text-dark-400">{album.year}</p>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-dark-400">No albums found</p>
        )}
      </div>

      {/* Artist songs */}
      <div className="mb-8 border-t border-dark-700 pt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
              <ListMusic size={20} className="text-primary-400" />
              All Songs
            </h2>
            <p className="text-sm text-dark-400">
              {tracksLoading ? 'Loading songs...' : `${sortedArtistTracks.length} song${sortedArtistTracks.length !== 1 ? 's' : ''}, grouped by album`}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePlayAll}
              disabled={sortedArtistTracks.length === 0}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              <Play size={16} />
              Play All
            </button>
            <button
              onClick={handleShuffleAll}
              disabled={sortedArtistTracks.length === 0}
              className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
            >
              <Shuffle size={16} />
              Shuffle All
            </button>
          </div>
        </div>

        {tracksLoading ? (
          <div className="flex items-center gap-2 text-dark-400 py-4">
            <Loader2 size={18} className="animate-spin" />
            <span>Loading songs...</span>
          </div>
        ) : tracksByAlbum.length > 0 ? (
          <div className="space-y-4">
            {tracksByAlbum.map((group) => (
              <section key={group.albumId} className="bg-dark-800/60 rounded-xl border border-dark-700 overflow-hidden">
                <div className="flex items-center gap-3 p-3 sm:p-4 border-b border-dark-700">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-dark-700 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                    {group.coverPath ? (
                      <img
                        src={`/api/image/${encodeURIComponent(group.coverPath)}`}
                        alt={group.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Disc3 size={24} className="text-dark-500" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate">{group.title}</h3>
                    <p className="text-xs sm:text-sm text-dark-400">
                      {group.year ? `${group.year} • ` : ''}{group.tracks.length} song{group.tracks.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="divide-y divide-dark-700/70">
                  {group.tracks.map((track, index) => (
                    <button
                      key={track.id}
                      onClick={() => playQueue(sortedArtistTracks, sortedArtistTracks.findIndex(t => t.id === track.id))}
                      className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 text-left hover:bg-dark-700/70 transition-colors"
                    >
                      <span className="w-8 text-sm text-dark-500 text-right">
                        {track.track_number ?? index + 1}
                      </span>
                      <span className="flex-1 min-w-0 truncate text-sm sm:text-base">{track.title}</span>
                      <span className="text-xs sm:text-sm text-dark-500">{formatDuration(track.duration_ms)}</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <p className="text-dark-400">No songs found for this artist</p>
        )}
      </div>

      {/* Missing Albums Section */}
      <div className="border-t border-dark-700 pt-6">
        <button
          onClick={() => setShowMissingAlbums(!showMissingAlbums)}
          className="flex items-center gap-2 text-lg font-semibold hover:text-primary-400 transition-colors"
        >
          <Sparkles size={20} className="text-yellow-400" />
          <span>Find More Albums</span>
          {showMissingAlbums ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        <p className="text-sm text-dark-400 mt-1">
          Search for albums by {artist.name} available for download
        </p>

        {showMissingAlbums && (
          <div className="mt-4">
            {dabSearching && (
              <div className="flex items-center gap-2 text-dark-400 py-4">
                <Loader2 size={18} className="animate-spin" />
                <span>Searching for albums...</span>
              </div>
            )}

            {!dabSearching && missingAlbums.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-dark-400 mb-3">
                  Found {missingAlbums.length} album{missingAlbums.length !== 1 ? 's' : ''} not in your library
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {missingAlbums.map((result) => (
                    <div
                      key={result.album_id}
                      className="bg-dark-800 rounded-lg p-4 flex gap-4"
                    >
                      <div className="w-24 h-24 flex-shrink-0 bg-dark-700 rounded overflow-hidden">
                        {result.cover_url ? (
                          <img
                            src={result.cover_url}
                            alt={result.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Disc3 size={32} className="text-dark-500" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <h4 className="font-medium truncate" title={result.title}>
                          {result.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-sm text-dark-400">
                          <span>{result.track_count} tracks</span>
                          {result.year && <span>• {result.year}</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-dark-500">
                            {result.bit_depth}bit / {(result.sample_rate / 1000).toFixed(1)}kHz
                          </span>
                          {result.is_hi_res && (
                            <span className="bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded text-[10px]">
                              Hi-Res
                            </span>
                          )}
                        </div>

                        {/* Download button */}
                        <div className="mt-auto pt-2">
                          {dabDownloadedIds.has(result.album_id) ? (
                            <div className="flex items-center gap-2 text-green-400">
                              <CheckCircle size={16} />
                              <span className="text-sm">Downloaded</span>
                            </div>
                          ) : dabDownloadErrors[result.album_id] ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-red-400">
                                <AlertCircle size={16} />
                                <span className="text-sm">Failed</span>
                              </div>
                              <p className="text-xs text-dark-500 line-clamp-2">
                                {dabDownloadErrors[result.album_id]}
                              </p>
                              <button
                                onClick={() => downloadDabMutation.mutate(result)}
                                className="text-xs text-primary-400 hover:text-primary-300"
                              >
                                Retry
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => downloadDabMutation.mutate(result)}
                              disabled={dabDownloadingIds.has(result.album_id)}
                              className="btn btn-sm btn-primary flex items-center gap-1.5"
                            >
                              {dabDownloadingIds.has(result.album_id) ? (
                                <>
                                  <Loader2 size={14} className="animate-spin" />
                                  <span>Downloading...</span>
                                </>
                              ) : (
                                <>
                                  <Download size={14} />
                                  <span>Download</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!dabSearching && dabResults && missingAlbums.length === 0 && (
              <div className="text-center py-8">
                <CheckCircle size={32} className="mx-auto mb-2 text-green-400" />
                <p className="text-dark-400">You have all available albums by this artist!</p>
              </div>
            )}

            {!dabSearching && !dabResults && (
              <div className="text-center py-8">
                <Search size={32} className="mx-auto mb-2 text-dark-500" />
                <p className="text-dark-400">No results found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
